import React, { useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { useTheme } from 'styled-components';
import { select, pointers } from 'd3';
import { max, min } from 'lodash';
import { useD3 } from '../../utils/hooks/useD3';
import {
  PASS_PLACEMENT_COLOR_MODES,
  PASS_PLACEMENT_CLASS_NAMES,
  CHART_AREA_SIZES,
  PASS_PLACEMENT_SCALES,
  PASS_PLACEMENT_SCALE_DEFAULTS,
} from './PassPlacement.constants';
import {
  getPalette,
  getEnrichedTheme,
} from '../../utils/visualisations/visPalettes';
import {
  drawPasses,
  drawGrid,
  drawCatcher,
  drawAxes,
} from './PassPlacement.drawing';
import { linearScaleBuilder } from '../../utils/visualisations/axes';
import { addBackgroundRect } from '../../utils/visualisations/basic';
import { drawPassPlacementKey } from './PassPlacementKey/PassPlacementKey';
import { partitionPassPlacementData } from './PassPlacement.dataManipulation';
import { getRectDimensions } from '../DragSelect/DragSelect.dataManipulation';

const PassPlacement = function ({
  data,
  showCatcher,
  showFailsRing,
  showBallSize,
  colorMode,
  showKey,
  margin,
  selectedPlay,
  handleEventSelection,
  aspectRatioName,
  plusMinusX,
  handleDragSelection,
  selectedPlays: selectedPlaysProp,
  redrawSelection,
  isInteractive,
}) {
  const { isDark } = useTheme();
  const kbTheme = useTheme();
  const theme = getEnrichedTheme(kbTheme);
  const visPalette = getPalette(isDark);
  const selectionEnabled = !!handleEventSelection; // if no selection function, selection is disabled

  // Multi-select refs
  const marqueeId = 'pass-placement-selection-marquee';
  const interacting = useRef(false);
  const originX = useRef(0);
  const originY = useRef(0);
  const passedX = useRef(0);
  const passedY = useRef(0);
  const savedX = useRef(0);
  const savedY = useRef(0);
  const savedWidth = useRef(0);
  const savedHeight = useRef(0);
  // multi select, selected plays
  const selectedPlays = useRef([]);

  // Marquee helper functions
  const resetMarquee = (svg) => {
    savedWidth.current = 0;
    savedHeight.current = 0;
    savedX.current = 0;
    savedY.current = 0;
    svg
      .select(`#${marqueeId}`)
      .attr('x', savedX.current)
      .attr('y', savedY.current)
      .attr('width', savedWidth.current)
      .attr('height', savedHeight.current);
  };

  const updateMarquee = (svg, mouseEvent) => {
    const p = pointers(mouseEvent);
    const [currentX, currentY] = p[0];
    passedX.current = currentX;
    passedY.current = currentY;

    if (interacting.current) {
      const { rectX, rectY, rectWidth, rectHeight } = getRectDimensions(
        originX.current,
        originY.current,
        passedX.current,
        passedY.current
      );
      savedWidth.current = rectWidth;
      savedHeight.current = rectHeight;
      savedX.current = rectX;
      savedY.current = rectY;
      svg
        .select(`#${marqueeId}`)
        .attr('fill', theme.colours.interface.main)
        .attr('stroke', theme.colours.interface.main)
        .attr('stroke-dasharray', 'none')
        .attr('x', rectX)
        .attr('y', rectY)
        .attr('width', rectWidth)
        .attr('height', rectHeight);
    } else {
      originX.current = passedX.current;
      originY.current = passedY.current;
    }
  };

  const completeMarquee = (svg, xScaleFn, yScaleInvertedFn) => {
    interacting.current = false;
    svg
      .select(`#${marqueeId}`)
      .attr('fill-opacity', 0.4)
      .attr('fill', 'transparent')
      .attr('stroke', visPalette.contrast)
      .attr('stroke-dasharray', '5 6')
      .attr('stroke-width', 2);

    // Compute bounds - explicitly store in variables to prevent optimization issues
    const minXs = min([originX.current, passedX.current]);
    const maxXs = max([originX.current, passedX.current]);
    const minYs = min([originY.current, passedY.current]);
    const maxYs = max([originY.current, passedY.current]);
    // Defensive check: ensure bounds are valid before filtering
    if (
      minXs === undefined ||
      maxXs === undefined ||
      minYs === undefined ||
      maxYs === undefined
    ) {
      return;
    }

    // Force bounds evaluation by creating a bounds object
    // This prevents JS engine from optimizing away the min/max calculations
    const bounds = { minXs, maxXs, minYs, maxYs };
    const { minXs: minX, maxXs: maxX, minYs: minY, maxYs: maxY } = bounds;
    const selectedPasses = data?.passEvents?.edges?.filter((edge) => {
      const eventX = xScaleFn(edge.node.passPlacementX);
      const eventY = yScaleInvertedFn(edge.node.passPlacementY);
      return (
        eventX >= minX && eventX <= maxX && eventY >= minY && eventY <= maxY
      );
    });

    if (handleDragSelection) {
      selectedPlays.current = selectedPasses?.map((d) => d.node.play?.id) || [];
      handleDragSelection(selectedPlays.current);
    }
  };

  const passPlacementChartWidth = CHART_AREA_SIZES.MAIN_WIDTH; // basic size choice
  const aspectRatio =
    PASS_PLACEMENT_SCALES.find((a) => a.ASPECT_RATIO === aspectRatioName) ||
    PASS_PLACEMENT_SCALES[0];
  const maxX =
    plusMinusX ||
    aspectRatio.PLUS_MINUS_X_OPTIONS[aspectRatio.DEFAULT_PLUS_MINUS_X_INDEX];
  const passPlacementChartHeight =
    passPlacementChartWidth * aspectRatio.RATIO_Y_OVER_X; // defaults to 3:2 ratio
  const maxY = maxX * 2 * aspectRatio.RATIO_Y_OVER_X;

  /* Define the scaling functions according to chart size */
  const xScale = linearScaleBuilder(-maxX, maxX, 0, passPlacementChartWidth);
  /* 
  Default scale for sizing things (player / football)
  Inverted scale for ground (y=0) at bottom 
  */
  const yScale = linearScaleBuilder(0, maxY, 0, passPlacementChartHeight);
  const yScaleInverted = linearScaleBuilder(
    maxY,
    0,
    0,
    passPlacementChartHeight
  );

  const chart_w = passPlacementChartWidth + CHART_AREA_SIZES.AXIS_SPACE;
  const chart_h = passPlacementChartHeight + CHART_AREA_SIZES.AXIS_SPACE;
  const key_margin_top = 10;
  const key_h = showKey ? key_margin_top + 30 : 0; // 30 for actual key stuff
  const vbw = chart_w + margin.left + margin.right;
  const vbh = chart_h + key_h + margin.top + margin.bottom;
  const viewBox = `0 0 ${vbw} ${vbh}`;

  const chartAreaClipPath = 'pass-placement-chart-area-clip-path';

  /* Static Bit - set up classes that get modded */
  const ref = useD3(
    (svg) => {
      // reset svg
      svg.selectAll('*').remove();

      svg
        .attr('id', 'pass-placement-vis')
        .attr('width', '100%')
        .attr('viewBox', viewBox);

      addBackgroundRect(svg, visPalette.background.main);

      const within_margin_transform = `translate(${margin.left},${margin.top})`;
      const marginAdjustedArea = svg
        .append('g')
        .attr('class', PASS_PLACEMENT_CLASS_NAMES.MARGIN)
        .attr('data-testid', 'marginAdjusted')
        .attr('transform', within_margin_transform);

      marginAdjustedArea
        .append('defs')
        .append('clipPath')
        .attr('id', chartAreaClipPath)
        .append('rect')
        .attr('x', 0)
        .attr('y', 0)
        .attr('width', passPlacementChartWidth)
        .attr('height', passPlacementChartHeight);

      /* Define area within the axes, and use clip-path to prevent overflow */
      const within_axis_transform = `translate(${CHART_AREA_SIZES.AXIS_SPACE},0)`;
      const chartAreaUnclipped = marginAdjustedArea
        .append('g')
        .attr('class', PASS_PLACEMENT_CLASS_NAMES.UNCLIPPED)
        .attr('transform', within_axis_transform);

      const chartAreaClipped = marginAdjustedArea
        .append('g')
        .attr('class', PASS_PLACEMENT_CLASS_NAMES.CLIPPED)
        .attr('transform', within_axis_transform)
        .attr('clip-path', `url(#${chartAreaClipPath})`);

      const clippedChild = chartAreaClipped.append('g');
      const axesZone = marginAdjustedArea
        .append('g')
        .attr('class', PASS_PLACEMENT_CLASS_NAMES.AXES)
        .attr('transform', within_axis_transform);

      // catcher is unclipped, bottom layer
      const catcherZone = chartAreaUnclipped
        .append('g')
        .attr('data-testid', 'pass-placement-catcher')
        .attr('class', PASS_PLACEMENT_CLASS_NAMES.CATCHER);
      catcherZone.call(
        drawCatcher,
        xScale,
        yScale,
        yScaleInverted,
        showCatcher,
        isDark
      );

      // clipped zone for grid/passes
      const gridZone = clippedChild
        .append('g')
        .attr('class', PASS_PLACEMENT_CLASS_NAMES.GRID);
      gridZone.call(drawGrid, visPalette, xScale, yScaleInverted);

      // interactive zone for event selection
      clippedChild
        .append('g')
        .attr('class', PASS_PLACEMENT_CLASS_NAMES.INTERACTIVE);

      // main data for passes
      clippedChild
        .append('g')
        .attr('class', PASS_PLACEMENT_CLASS_NAMES.DOTS_PLAYER)
        .attr('data-testid', 'pass-placement-dots-zone');

      /* axes layer unclipped above grid */
      axesZone.call(drawAxes, visPalette, xScale, yScaleInverted, maxX, maxY);

      /* Add a <g> for the key */
      marginAdjustedArea
        .append('g')
        .attr('class', PASS_PLACEMENT_CLASS_NAMES.KEY)
        .attr(
          'transform',
          `translate(${CHART_AREA_SIZES.AXIS_SPACE},${
            chart_h + key_margin_top
          })`
        );
    },
    [isDark]
  );

  /* When things change: redraw */
  useEffect(() => {
    const svg = select(ref.current);

    svg.attr('viewBox', viewBox);

    const clipPath = svg.select(`#${chartAreaClipPath}`);
    clipPath.selectAll('rect').remove();
    clipPath
      .append('rect')
      .attr('x', 0)
      .attr('y', 0)
      .attr('width', passPlacementChartWidth)
      .attr('height', passPlacementChartHeight);

    svg
      .select(`.${PASS_PLACEMENT_CLASS_NAMES.CATCHER}`)
      .call(drawCatcher, xScale, yScale, yScaleInverted, showCatcher, isDark);
    svg
      .select(`.${PASS_PLACEMENT_CLASS_NAMES.AXES}`)
      .call(drawAxes, visPalette, xScale, yScaleInverted, maxX, maxY);
    svg
      .select(`.${PASS_PLACEMENT_CLASS_NAMES.GRID}`)
      .call(drawGrid, visPalette, xScale, yScaleInverted);

    if (data) {
      const interactiveLayer = svg.select(
        `.${PASS_PLACEMENT_CLASS_NAMES.INTERACTIVE}`
      );
      interactiveLayer.selectAll('*').remove();
      /* Add marquee rectangle for drag selection */
      interactiveLayer.append('rect').attr('id', marqueeId);

      /* Initialize marquee styling */
      interactiveLayer
        .select(`#${marqueeId}`)
        .attr('fill-opacity', 0.4)
        .attr('fill', 'transparent')
        .attr('stroke', visPalette.contrast)
        .attr('stroke-dasharray', '5 6')
        .attr('stroke-width', 2)
        .attr('x', redrawSelection ? savedX.current : 0)
        .attr('y', redrawSelection ? savedY.current : 0)
        .attr('width', redrawSelection ? savedWidth.current : 0)
        .attr('height', redrawSelection ? savedHeight.current : 0);

      /* Add drag area for multi-select - extended 40px above chart for top edge selection */
      const dragAreaExtension = 40;
      interactiveLayer
        .append('g')
        .append('rect')
        .attr('x', 0)
        .attr('y', 0 - dragAreaExtension)
        .attr('width', passPlacementChartWidth)
        .attr('height', passPlacementChartHeight + dragAreaExtension)
        .attr('fill', 'transparent')
        .attr('stroke', 'none')
        .attr('class', isInteractive ? 'drawable' : '')
        .attr('id', 'pass-placement-drag-area')
        .on('click', () => {
          if (isInteractive && !interacting.current) {
            handleEventSelection(null);
          }
        })
        .on('mousedown', () => {
          if (isInteractive) {
            interacting.current = true;
            handleEventSelection(null);
            resetMarquee(svg);
            // Swap stacking order: move interactive layer above dots
            svg.select(`.${PASS_PLACEMENT_CLASS_NAMES.INTERACTIVE}`).raise();
          }
        })
        .on('mousemove', (mouseEvent) => {
          updateMarquee(svg, mouseEvent);
        })
        .on('mouseup', (e) => {
          e.stopPropagation();
          completeMarquee(svg, xScale, yScaleInverted);
          // Restore stacking order: move dots layer above interactive
          svg.select(`.${PASS_PLACEMENT_CLASS_NAMES.DOTS_PLAYER}`).raise();
        });

      /** Partition the data into focus and background sets */
      const { focusData, backgroundData } = partitionPassPlacementData(
        data,
        selectedPlaysProp || []
      );

      const circlesG = svg.select(`.${PASS_PLACEMENT_CLASS_NAMES.DOTS_PLAYER}`);
      drawPasses(
        circlesG,
        visPalette,
        selectedPlay,
        colorMode,
        showFailsRing,
        showBallSize,
        xScale,
        yScale,
        yScaleInverted,
        selectionEnabled,
        handleEventSelection,
        focusData,
        backgroundData
      );
    }
  }, [
    data,
    showCatcher,
    showFailsRing,
    showBallSize,
    colorMode,
    selectedPlay,
    aspectRatioName,
    plusMinusX,
    showKey,
    isDark,
    selectedPlaysProp,
    redrawSelection,
  ]);

  /* Redraw key has fewer dependencies */
  useEffect(() => {
    const svg = select(ref.current);
    const keyG = svg.select(`.${PASS_PLACEMENT_CLASS_NAMES.KEY}`);
    keyG.attr(
      'transform',
      `translate(${CHART_AREA_SIZES.AXIS_SPACE},${chart_h + key_margin_top})`
    );
    keyG.selectAll('g').remove();
    if (showKey) {
      drawPassPlacementKey(
        keyG,
        colorMode,
        visPalette,
        isDark,
        passPlacementChartWidth,
        showFailsRing,
        showBallSize
      );
    }
  }, [
    showKey,
    colorMode,
    aspectRatioName,
    plusMinusX,
    showFailsRing,
    showBallSize,
    isDark,
  ]);

  useEffect(() => {
    selectedPlays.current = selectedPlaysProp;
  }, [selectedPlaysProp]);

  return (
    <svg ref={ref} data-testid="passPlacementChart" id="passPlacementChart" />
  );
};

PassPlacement.propTypes = {
  // eslint-disable-next-line react/forbid-prop-types
  data: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
  showCatcher: PropTypes.bool,
  showFailsRing: PropTypes.bool,
  showBallSize: PropTypes.bool,
  colorMode: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.shape({
      label: PropTypes.string,
      value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    }),
  ]),
  aspectRatioName: PropTypes.string,
  plusMinusX: PropTypes.number,
  showKey: PropTypes.bool,
  margin: PropTypes.shape({
    bottom: PropTypes.number,
    left: PropTypes.number,
    right: PropTypes.number,
    top: PropTypes.number,
  }),
  // eslint-disable-next-line react/forbid-prop-types
  selectedPlay: PropTypes.object,
  handleEventSelection: PropTypes.func,
  handleDragSelection: PropTypes.func,
  selectedPlays: PropTypes.arrayOf(PropTypes.string),
  redrawSelection: PropTypes.bool,
  isInteractive: PropTypes.bool,
};

PassPlacement.defaultProps = {
  data: null,
  showCatcher: false,
  showFailsRing: true,
  showBallSize: true,
  colorMode: PASS_PLACEMENT_COLOR_MODES.LOCATION,
  aspectRatioName: PASS_PLACEMENT_SCALE_DEFAULTS.ASPECT_RATIO,
  plusMinusX: null,
  showKey: false,
  margin: { top: 20, right: 20, bottom: 10, left: 10 }, // minimal margins as axis "feel" like space
  selectedPlay: null,
  handleEventSelection: () => {},
  handleDragSelection: () => {},
  selectedPlays: [],
  redrawSelection: true,
  isInteractive: true,
};

export default PassPlacement;
